import axios from 'axios';
import logger from '../utils/logger';

// 配置API地址：优先使用环境变量 REACT_APP_API_URL（生产与开发均可）
// 若未设置，则生产环境在 mz2025.com 域名下回退到 https://api.mz2025.com，
// 其他域名仍保持相对路径以支持同源代理；开发环境使用 localhost:8000
const ENV_API_URL = (process.env.REACT_APP_API_URL || '').trim();
const isProduction = process.env.NODE_ENV === 'production' || window.location.protocol === 'https:';
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

let API_BASE_URL = '';
if (ENV_API_URL !== '') {
  API_BASE_URL = ENV_API_URL;
} else if (isProduction) {
  const isMZProd = hostname === 'mz2025.com' || hostname === 'www.mz2025.com';
  API_BASE_URL = isMZProd ? 'https://api.mz2025.com' : '';
} else {
  API_BASE_URL = 'http://localhost:8000';
}

// 导出API_BASE_URL供其他组件使用
export const API_URL = API_BASE_URL;

// 只在开发环境输出API配置（生产环境不暴露后端IP）
if (process.env.NODE_ENV === 'development') {
  logger.log('🔧 API配置:', {
    REACT_APP_API_URL: process.env.REACT_APP_API_URL,
    API_BASE_URL,
    currentOrigin: window.location.origin,
    hostname,
    isProduction: window.location.protocol === 'https:',
  });
}

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 增加到30秒，视频生成可能需要更长时间
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器：添加认证token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    // 如果是 FormData（文件上传），删除 Content-Type，让浏览器自动设置 multipart/form-data boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    // 调试：只在开发环境输出请求信息（生产环境不暴露完整URL）
    if (process.env.NODE_ENV === 'development') {
    logger.log('📤 API请求:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      fullURL: `${config.baseURL}${config.url}`,
      hasToken: !!token,
    });
    }
    return config;
  },
  (error) => {
    console.error('请求拦截器错误:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器：处理认证错误
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // 静默处理通知API的超时错误（已经在前端处理并返回0）
    if (error.config?.url?.includes('/notifications/unread-count') && 
        (error.code === 'ECONNABORTED' || error.message?.includes('timeout'))) {
      // 静默处理，不输出错误日志
      return Promise.reject(error);
    }
    
    // 只在关键错误时输出日志
    if (error.response?.status === 401 || error.response?.status === 500 || !error.response) {
      console.error(`API错误: ${error.config?.method?.toUpperCase()} ${error.config?.url}`, error.response?.status, error.message);
    }
    
    // 处理401认证错误
    if (error.response?.status === 401) {
      const errorData = error.response?.data;
      const errorMessage = errorData?.detail || errorData?.error || '';
      const responseHeaders = error.response?.headers || {};
      
      // 检查是否是被踢下线
      // 方式1: 检查响应头中的标识
      const isKickedOutByHeader = responseHeaders['x-kicked-out'] === 'true' || 
                                   responseHeaders['X-Kicked-Out'] === 'true';
      
      // 方式2: 检查错误信息中的关键词
      const errorMsgLower = errorMessage.toLowerCase();
      const isKickedOutByMessage = errorMsgLower.includes('device') || 
                                   errorMsgLower.includes('session') ||
                                   errorMsgLower.includes('revoked') ||
                                   errorMsgLower.includes('踢下线') ||
                                   errorMsgLower.includes('登录限制') ||
                                   errorMsgLower.includes('已在其他设备登录') ||
                                   errorMsgLower.includes('会话已失效');
      
      const isKickedOut = isKickedOutByHeader || isKickedOutByMessage;
      
      // 如果是在应用页面且是被踢下线，清除token并触发事件
      const isAppPage = window.location.pathname.startsWith('/app');
      if (isKickedOut && isAppPage) {
        // 清除token和会话数据
        localStorage.removeItem('access_token');
        sessionStorage.clear();
        
        // 触发自定义事件，通知应用显示弹窗并跳转
        window.dispatchEvent(new CustomEvent('userKickedOut', { 
          detail: { 
            message: '您的账号已在其他设备登录，当前会话已失效，请重新登录' 
          } 
        }));
        
        logger.warn('⚠️ API Client: 检测到被踢下线，已清除token并触发事件');
      } else {
        // 普通401错误，只记录日志（保持原有逻辑）
        const isLoginPage = window.location.pathname === '/login';
        const isHomePage = window.location.pathname === '/' || window.location.pathname === '';
        
        // 详细调试信息（脱敏处理）
        const token = localStorage.getItem('access_token');
        logger.error('🔴 API Client: 401认证失败详细诊断', {
          path: window.location.pathname,
          isAppPage,
          isLoginPage,
          isHomePage,
          requestUrl: error.config?.url,
          requestMethod: error.config?.method,
          tokenExists: !!token,
          errorResponse: error.response?.data,
          errorStatus: error.response?.status,
          errorStatusText: error.response?.statusText,
          timestamp: new Date().toISOString()
        });
        
        logger.warn('⚠️ API Client: 检测到401错误，但暂时不清除token，避免立即被踢出');
        logger.warn('⚠️ 如果多次出现401错误，请手动检查token是否有效');
      }
    }
    return Promise.reject(error);
  }
);

export default api;
